import { useRefs } from 'contexts/RefContext';
import { useKonvaImageScale } from 'contexts/ToolDimensionsAndScaleContext';
import Konva from 'konva';
import { useCallback, useEffect, useRef, useState } from 'react';
import { Group } from 'react-konva';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from 'store';
import { Tree, addTree, setSelectedShapeId, setDeleted, setIsDrawingTree,
	ShapeId, setDraftTree, cancelDraftTree } from 'store/slices/TreeSlice';
import { TREE_CONFIG } from 'components/ThreeDImage/treeConfig';
import TreeNode from './TreeNode';
import { selectTreeState, selectTreeEditingMode, selectTreeDeleteMode, 
	selectIsDrawingTree, selectIsFlattenAreaMode } from 'store/slices/TreeSlice/selector';

export default function TreeDrawer() {
	const { selectedTreeId, trees } = useSelector(selectTreeState);
	const treeEditingMode = useSelector(selectTreeEditingMode);
	const deleteMode = useSelector(selectTreeDeleteMode);
	const isDrawingTree = useSelector(selectIsDrawingTree);
	const { moveStage, rotateStage, editModeEnabled, hideSatelliteOverlay } = useSelector((state: RootState) => state.toolNewPostions.data);
	const isFlattenAreaMode = useSelector(selectIsFlattenAreaMode);
	const { groundMetersPerPixel } = useSelector((state: RootState) => state.roofData.data);
	const isDraftingTree = useSelector((state: RootState) => state.treeSlice.data.isDraftingTree);

	const [inProgressTree, setInProgressTree] = useState<Tree>();
	const [hoveredTreeId, setHoveredTreeId] = useState<ShapeId | null>(null);

	const { konvaRef } = useRefs();
	const baseImageScale = useKonvaImageScale();
	const dispatch = useDispatch();

	const treeGroupRef = useRef<Konva.Group | null>(null);
	const drawingIdRef = useRef<string | null>(null);
	const isClickMoveClickRef = useRef(false);
	const isPinchingRef = useRef(false); // true during a pinch (2+ fingers)

	const getBaseCursor = useCallback(() => {
		if (moveStage || rotateStage) return 'grab';
		if (!treeEditingMode) return 'default';
		if (deleteMode) return 'default';
		if (isFlattenAreaMode) return 'crosshair';
		return 'crosshair';
	}, [moveStage, rotateStage, treeEditingMode, deleteMode, isFlattenAreaMode]);
	const setStageCursor = useCallback((cursor: string) => {
		const stage = konvaRef?.current;
		if (!stage) return;
		stage.container().style.cursor = cursor;
	}, [konvaRef]);

	const handlePointerDown = useCallback((e: Konva.KonvaEventObject<PointerEvent>) => {
		if (deleteMode || moveStage || rotateStage || isFlattenAreaMode) return;
		if (isClickMoveClickRef.current) return;
		if (inProgressTree) return; // a pointerdown here is only ever a 2nd finger (pinch) — ignore
		const pos = e.target.getStage()?.getRelativePointerPosition();
		if (!pos) return;

		const uuid = crypto.randomUUID();
		drawingIdRef.current = uuid;
		const imagePos = {
			x: pos.x / baseImageScale.x,
			y: pos.y / baseImageScale.y,
		};

		setInProgressTree({
			id: uuid,
			...imagePos,
			crownRadius: 0,
			trunkRadius: 0,
			treeHeight: 0,
			crownHeight: 0,
			crownShape: 'sphere',
		});

	}, [baseImageScale.x, baseImageScale.y, deleteMode, moveStage, rotateStage, isFlattenAreaMode, inProgressTree]);

	const handlePointerMove = useCallback((e: Konva.KonvaEventObject<PointerEvent>) => {
		if (!inProgressTree) return;
		if (isPinchingRef.current) return; // pinch — ignore its move noise
		// map only: the in-progress tree's radius is purely state-driven off this handler —
		// freeze it by simply not updating that state while hidden, so it sits exactly as
		// left and resumes following the cursor on the next real move after exit.
		if (hideSatelliteOverlay) return;
		const pos = e.target.getStage()?.getRelativePointerPosition();
		if (!pos) return;

		const current = {
			x: pos.x / baseImageScale.x,
			y: pos.y / baseImageScale.y,
		};

		const dx = current.x - inProgressTree.x;
		const dy = current.y - inProgressTree.y;
		const radius = Math.sqrt(dx * dx + dy * dy); // true euclidean radius

		if (!isDrawingTree && radius > 0) {
			dispatch(setIsDrawingTree(true));
		}
		setInProgressTree(prev => prev && {
			...prev,
			crownRadius: radius,
			trunkRadius: radius * TREE_CONFIG.trunkRadiusRatio,
		});
	}, [baseImageScale.x, baseImageScale.y, dispatch, inProgressTree, isDrawingTree, hideSatelliteOverlay]);

	const handlePointerUp = useCallback((e: Konva.KonvaEventObject<PointerEvent>) => {
		if (!inProgressTree) return;
		if (isPinchingRef.current) return; // pinch — don't finalize/discard
		if (inProgressTree.crownRadius > 0) {
			const crownRadiusM = inProgressTree.crownRadius * groundMetersPerPixel;

			// TODO: constants
			const treeHeightM = crownRadiusM * 3.33;
			const crownRatio = 0.4;

			const crownHeight = treeHeightM * crownRatio;

			dispatch(addTree({ ...inProgressTree, treeHeight: treeHeightM, crownHeight }));
			dispatch(setSelectedShapeId(inProgressTree.id));
			setInProgressTree(undefined);
			dispatch(setIsDrawingTree(false));
			isClickMoveClickRef.current = false;
			return;
		}

		// crownRadius is 0: no drag occurred.
		if (!isClickMoveClickRef.current) {
			if (e.evt?.pointerType === 'touch') {
				setInProgressTree(undefined);
				return;
			}
			isClickMoveClickRef.current = true;
			dispatch(setDraftTree());
			return;
		}

		isClickMoveClickRef.current = false;
		setInProgressTree(undefined);
		dispatch(setIsDrawingTree(false));
		dispatch(cancelDraftTree());
	}, [dispatch, groundMetersPerPixel, inProgressTree]);

	// Pinch detection: native touches.length
	const handleTouchStart = useCallback((e: Konva.KonvaEventObject<TouchEvent>) => {
		if ((e.evt.touches?.length ?? 0) >= 2) isPinchingRef.current = true;
	}, []);
	const handleTouchEnd = useCallback((e: Konva.KonvaEventObject<TouchEvent>) => {
		if ((e.evt.touches?.length ?? 0) === 0) isPinchingRef.current = false;
	}, []);

	useEffect(() => {
		const stage = konvaRef?.current;
		if (!stage || !treeEditingMode || deleteMode || moveStage || rotateStage || isFlattenAreaMode) return;

		stage.on('pointerdown', handlePointerDown);
		stage.on('pointerup', handlePointerUp);
		stage.on('pointermove', handlePointerMove);
		stage.on('touchstart', handleTouchStart);
		stage.on('touchmove', handleTouchStart);
		stage.on('touchend', handleTouchEnd);
		stage.on('touchcancel', handleTouchEnd);

		return () => {
			stage.off('pointerdown', handlePointerDown);
			stage.off('pointerup', handlePointerUp);
			stage.off('pointermove', handlePointerMove);
			stage.off('touchstart', handleTouchStart);
			stage.off('touchmove', handleTouchStart);
			stage.off('touchend', handleTouchEnd);
			stage.off('touchcancel', handleTouchEnd);
		};
	}, [
		handlePointerUp, handlePointerDown, handlePointerMove, handleTouchStart, handleTouchEnd,
		konvaRef, treeEditingMode, deleteMode, moveStage, rotateStage, isFlattenAreaMode,
	]);

	const handleTreePointerDown = useCallback((e: Konva.KonvaEventObject<PointerEvent>, id: ShapeId) => {
		if (!id) return;
		if (!treeEditingMode) return;
		if (isFlattenAreaMode) return;
		e.cancelBubble = true;

		if (deleteMode) {
			dispatch(setDeleted(id));
			if (selectedTreeId === id) {
				dispatch(setSelectedShapeId(null));
			}
			setStageCursor(getBaseCursor());
			return;
		}

		dispatch(setSelectedShapeId(id));
	}, [deleteMode, dispatch, getBaseCursor, selectedTreeId, setStageCursor, treeEditingMode, isFlattenAreaMode]);

	const canHoverTrees = treeEditingMode && !isFlattenAreaMode;

	const handleTreePointerEnter = useCallback((id: ShapeId) => {
		if (!canHoverTrees || !id) return;
		setHoveredTreeId(id);
		setStageCursor('pointer');
	}, [canHoverTrees, setStageCursor]);

	const handleTreePointerLeave = useCallback((id: ShapeId) => {
		if (!canHoverTrees || !id) return;
		setHoveredTreeId((prev) => (prev === id ? null : prev));
		setStageCursor(getBaseCursor());
	}, [canHoverTrees, getBaseCursor, setStageCursor]);

	useEffect(() => {
		if (!isFlattenAreaMode) return;
		if (inProgressTree) {
			isClickMoveClickRef.current = false;
			setInProgressTree(undefined);
			dispatch(setIsDrawingTree(false));
		}
	}, [dispatch, inProgressTree, isFlattenAreaMode]);

	useEffect(() => {
		if (!isDraftingTree && isClickMoveClickRef.current) {
			isClickMoveClickRef.current = false;
			setInProgressTree(undefined);
			dispatch(setIsDrawingTree(false));
		}
	}, [isDraftingTree, dispatch]);

	useEffect(() => {
		if (!editModeEnabled || !treeEditingMode || isFlattenAreaMode) return;
		setStageCursor(getBaseCursor());
	}, [editModeEnabled, getBaseCursor, isFlattenAreaMode, setStageCursor, treeEditingMode]);

	useEffect(() => {
		if (canHoverTrees) return;
		setHoveredTreeId(null);
	}, [canHoverTrees]);

	if (!editModeEnabled) return null;

	return (

		<Group
			name='tree-drawer'
			ref={treeGroupRef}
			scale={baseImageScale}
		>
			{trees.map(tree => {
				const isSelected = !!selectedTreeId && tree.id === selectedTreeId;
				const isHovered = canHoverTrees && hoveredTreeId === tree.id;
				return (<TreeNode
					key={tree.id}
					tree={tree}
					isSelected={isSelected}
					isHovered={isHovered}
					onPointerDown={(e) => handleTreePointerDown(e, tree.id)}
					onPointerEnter={() => handleTreePointerEnter(tree.id)}
					onPointerLeave={() => handleTreePointerLeave(tree.id)}
				/>);
			})}

			{/* Local in progress circle(Tree 2d) */}
			{inProgressTree && (
				<TreeNode tree={inProgressTree} />
			)}

		</Group>
	);
}
